import type { ReactNode } from "react";
import styled from "@emotion/styled";

import proactiveTrialMacosImageUrl from "@/assets/images/onboarding/proactive-trial-macos.png";
import proactiveTrialWindowsImageUrl from "@/assets/images/onboarding/proactive-trial-windows.png";
import {
  PaywallPlanAction,
  PaywallPlanCard,
} from "@/components/modal/paywall/paywall-modal-components";
import {
  buildProactivePlanCard,
  getProactivePlanActionCopy,
} from "@/components/modal/proactive-upsell/proactive-plan";
import { MDSLink, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { clientEnvModule } from "@/modules/client-env";

export interface ProactiveTrialPitchProps {
  actionNote?: string;
  heading?: string;
  isActionLoading?: boolean;
  isTrialEligible: boolean;
  isWindows?: boolean;
  learnMoreHref?: string;
  nonSkippableTrialAssignmentIsLoading?: boolean;
  planFooter?: ReactNode;
  proactivePrice?: string;
  pricingError?: string;
  pricingIsLoading?: boolean;
  onLearnMore?: () => void;
  onRetryPricing: () => void;
  onSubscribe: () => void;
}

/** Resolves the preview image for the caller-provided or detected platform. */
const getProactiveTrialImageUrl = ({
  shouldShowWindowsImage,
}: {
  shouldShowWindowsImage: boolean;
}): string => {
  if (shouldShowWindowsImage) {
    return proactiveTrialWindowsImageUrl;
  }

  return proactiveTrialMacosImageUrl;
};

/**
 * Renders the Proactive trial pitch shared by onboarding and upsell modals.
 *
 * Callers own their surface-specific heading, action note, loading state, and
 * optional footer, while this component keeps the product copy, preview image,
 * plan features, and trial action copy consistent.
 */
export const ProactiveTrialPitch = ({
  actionNote,
  heading,
  isActionLoading = false,
  isTrialEligible,
  isWindows,
  learnMoreHref,
  nonSkippableTrialAssignmentIsLoading = false,
  planFooter,
  proactivePrice,
  pricingError,
  pricingIsLoading = false,
  onLearnMore,
  onRetryPricing,
  onSubscribe,
}: ProactiveTrialPitchProps) => {
  const planCard = buildProactivePlanCard({
    density: "compact",
    isTrialEligible,
    proactivePrice,
    pricingError,
  });
  const actionCopy = getProactivePlanActionCopy({
    isTrialEligible,
    proactivePrice,
    pricingError,
    pricingIsLoading,
  });
  const defaultActionNote = (() => {
    if (actionCopy.pricingUnavailable || !isTrialEligible || !proactivePrice) {
      return actionCopy.note;
    }

    return `Then ${proactivePrice}. Cancel anytime.`;
  })();
  const resolvedActionNote = actionCopy.pricingUnavailable
    ? actionCopy.note
    : (actionNote ?? defaultActionNote);
  const shouldShowWindowsImage = isWindows ?? clientEnvModule.isWindows();
  const proactiveTrialImageUrl = getProactiveTrialImageUrl({ shouldShowWindowsImage });
  const resolvedHeading =
    heading ?? (isTrialEligible ? "Mem Agent is better with Proactive" : "Upgrade to Proactive");

  return (
    <TrialSurface>
      <TrialLayout data-proactive-trial-layout>
        <CopyColumn>
          <TrialHeading variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
            {resolvedHeading}
          </TrialHeading>

          <ProactiveTrialImage
            src={proactiveTrialImageUrl}
            alt="Mem Proactive reminder preview"
            $isWindows={shouldShowWindowsImage}
          />

          <BodyCopy variant={MDSTextVariant.ParagraphSm}>
            On Free, Mem Agent answers when you ask. With Proactive, it tracks follow-ups,
            deadlines, and loose ends from your notes and meetings — plus anything you tell it to
            remember — and reminds you at the right time.
          </BodyCopy>
          <BodyCopy variant={MDSTextVariant.ParagraphSm}>
            Free includes 25 notes and 25 Mem Agent messages per month.
          </BodyCopy>

          <LearnMoreLink
            href={learnMoreHref}
            target={learnMoreHref ? "_blank" : undefined}
            rel={learnMoreHref ? "noopener noreferrer" : undefined}
            onPress={onLearnMore}
            variant={MDSTextVariant.ParagraphSm}
          >
            Learn more about Mem Agent ↗
          </LearnMoreLink>
        </CopyColumn>

        <PlanColumn data-proactive-trial-plan-column>
          <PaywallPlanCard planCard={planCard} />
        </PlanColumn>
      </TrialLayout>

      <TrialActionGrid>
        <TrialActionColumn data-proactive-trial-action-column>
          <PaywallPlanAction
            buttonLabel={actionCopy.buttonLabel}
            isLoading={isActionLoading || pricingIsLoading || nonSkippableTrialAssignmentIsLoading}
            note={resolvedActionNote}
            onClick={actionCopy.pricingUnavailable ? onRetryPricing : onSubscribe}
          />

          {planFooter}
        </TrialActionColumn>
      </TrialActionGrid>
    </TrialSurface>
  );
};

const TrialSurface = styled.div`
  display: grid;
  flex: 0 1 auto;
  grid-template-columns: minmax(0, 428px) minmax(0, 365px);
  grid-template-rows: auto auto;
  column-gap: 40px;
  row-gap: 10px;
  box-sizing: border-box;
  width: 100%;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 950px) {
    display: flex;
    flex-direction: column;
  }
`;

const TrialLayout = styled.div`
  display: contents;

  @media (max-width: 950px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 40px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      display: block;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: ${({ theme }) => theme.borderRadius.medium};
      background: ${({ theme }) => theme.colors.border.default};
    }
  }
`;

const CopyColumn = styled.div`
  display: flex;
  grid-column: 1;
  grid-row: 1 / 3;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 950px) {
    grid-column: 1;
    grid-row: auto;
    min-height: auto;
    overflow: visible;
  }
`;

const TrialHeading = styled(MDSText)`
  display: block;
  color: ${({ theme }) => theme.colors.text.contrast};
  white-space: pre-line;
  word-break: break-word;
`;

const ProactiveTrialImage = styled.img<{ $isWindows?: boolean }>`
  display: block;
  align-self: center;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: ${({ $isWindows }) => ($isWindows ? "221px" : "174px")};
  border: ${({ theme, $isWindows }) =>
    $isWindows ? "none" : `1px solid ${theme.colors.border.default}`};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const BodyCopy = styled(MDSText)`
  width: 100%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LearnMoreLink = styled(MDSLink)`
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration-line: underline;
  text-decoration-style: dotted;
  text-underline-offset: 5px;

  &:hover {
    color: ${({ theme }) => theme.colors.text.link.hover};
  }
`;

const PlanColumn = styled.div`
  grid-column: 2;
  grid-row: 1;
  flex: none;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > section {
    box-sizing: border-box;
    max-height: 536px;
  }

  @media (max-width: 950px) {
    grid-column: 1;
    grid-row: auto;
    flex: none;
    height: auto;
    min-height: auto;
    overflow: visible;
  }
`;

const TrialActionGrid = styled.div`
  display: contents;

  @media (max-width: 950px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    flex-shrink: 0;
  }
`;

const TrialActionColumn = styled.div`
  display: flex;
  grid-column: 2;
  grid-row: 2;
  flex-direction: column;
  gap: 10px;
  min-width: 0;

  @media (max-width: 950px) {
    grid-column: 1;
    grid-row: auto;
  }
`;
